// @safetyhub/report-composer — 바인딩 resolver (Phase 2)
//
// ReportTemplate의 노드 트리를 순회하며 각 노드의 binding을 처리해
// RenderData(bindingKey → 값) 맵을 만든다. "단순 필드 추출"을 메타(binding) 기반으로
// 일반화하는 것이 목적 — 도메인별 adapter의 단순 필드 코드를 제거한다.
//
// 2모드(결정 기록 2026-06-19 §4):
//   - render(상세): binding.source대로 content에서 값을 읽는다(작성 시 박제된 snapshot).
//   - compose(작성): 작성폼 입력값/사전수집 system·ai 값을 content(snapshot)에 박는다.
//     기록 경로 = render의 읽기 경로(api.select ?? bindingKey)와 동일 → 라운드트립 보장.
//
// "가공"(여러 배열 zip, 가변열 매트릭스 등)은 resolver가 다루지 않는다 — 도메인 transform
// 함수가 담당하고 ComposerTestPage에서 base(resolver) + transform를 머지한다.

import type {
  ComposerNode,
  ReportTemplate,
  Binding,
  CatalogIndex,
  BindingFormat,
} from '@safetyhub/shared';
import {
  OVERLAY_SHAPES_CONTENT_KEY,
  OVERLAY_STROKES_CONTENT_KEY,
  SLOT_SIGNATURES_CONTENT_KEY,
  getByPath,
} from '@safetyhub/shared';
import type { RenderData } from './types';
import { runAssembler } from './assemblers';
// 기간(date-range) 값 판정/결합 — 표시 형식 kind:'date'가 기간 객체도 받는다(순수 모듈, 순환 없음).
import { DATE_DISPLAY_PATTERN_DEFAULT, formatDateRange, isDateRangeValue } from './dateValue';

// getByPath(dotted-path·`[key=value]` 조건 접근·proto 가드)는 `@safetyhub/shared`
// (readBindingValue.ts, 순수 모듈)로 이관(2026-08-24) — 렌더·검증(rc requiredValidation·서버
// composed-required-validation.util)이 같은 읽기 규약을 쓰기 위함(evaluateCondition 이관과 같은
// 규약). 기존 import 경로(`'./resolver'`) 유지를 위한 재-export.
export { getByPath };

/**
 * content 스냅샷의 박제 키. 조건 구문(`[key=value]`)이 들어간 select는 setByPath가
 * 재구성할 수 없어 content 키로 부적합 → bindingKey로 키잉(라운드트립 보존).
 * 단순 경로(또는 select 없음)는 종전대로 select(없으면 bindingKey)로 키잉(하위호환).
 * (export: methodReresolveMerge가 방식 재resolve 교체 키를 같은 철자로 도출하는 데 쓴다.)
 */
export function snapshotKey(binding: { select?: string; bindingKey: string }): string {
  const sel = binding.select;
  return sel && !sel.includes('[') ? sel : binding.bindingKey;
}

// ============================================================
// 범용 표시 포맷(format) — resolve된 원자값의 *표시용* 후처리
// ============================================================
//
// 도메인 무관 일반 연산만 수행한다(보고서 특정 키/로직 하드코딩 금지). render 표시 전용 —
// compose(write)에는 적용하지 않는다(원값 저장 → 라운드트립 보존). scope는 unitPath/template
// 의 보조 경로(같은 RenderData/content) 조회용으로만 쓴다.

/**
 * 값이 표시상 "빈값"인지(null/undefined/'') 판정.
 * visibleWhen 평가(`!isEmptyValue(values[visibleWhen])` — shared ComposerWidgetBase 규약)도
 * 이 판정을 재사용한다(결정: 2026-07-16-composer-combobox-dependent-value-axes P3).
 */
export function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

// 조건(visibleWhen/enabledWhen) 평가용 "빈값" 판정 — isEmptyValue 규약 + 빈 배열.
// 서버 완료 검증과 공유하기 위해 `@safetyhub/shared`(composer-write-validation, 순수 모듈)로
// 이관(2026-08-24) — 기존 import 경로 유지를 위한 재-export(evaluateCondition 이관과 같은 규약).
// 표시 format(empty/fallback)의 빈값 판정은 위 isEmptyValue 유지 — 조건 평가 전용 확장.
export { isEmptyConditionValue } from '@safetyhub/shared';

/**
 * 값을 표시 문자열로 강제(빈값은 ''). 객체/배열은 format 비대상이므로 String() 위임.
 *
 * **공개한다** — 형식의 경로 표현식(unitPath/switch.on/`{a.b}`)을 저작 화면에서 미리 해소해
 * 보여주는 호스트가 자기 판 문자 변환을 만들면, 렌더와 미리보기가 조용히 갈라진다
 * (`withFormatScope` 공개 사유와 같다 — index.ts 주석 참조).
 */
export function toText(value: unknown): string {
  if (isEmptyValue(value)) return '';
  return String(value);
}

/**
 * 경량 날짜 포맷터 — pattern 토큰을 치환한다(범용/순수, timezone 의존 없음).
 *
 * 지원 토큰: YYYY(4자리연), MM(2자리월), M(월), DD(2자리일), D(일),
 *            HH/H(시), mm/m(분), ss/s(초). 잘못된 날짜면 원값 문자열 반환.
 * 기본 pattern: `DATE_DISPLAY_PATTERN_DEFAULT`(dateValue — 표기 정본 한 곳).
 */
function formatDate(value: unknown, pattern: string): string {
  const raw = value instanceof Date ? value : new Date(value as string | number);
  if (Number.isNaN(raw.getTime())) return toText(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  const map: Record<string, string> = {
    YYYY: String(raw.getFullYear()),
    MM: pad(raw.getMonth() + 1),
    M: String(raw.getMonth() + 1),
    DD: pad(raw.getDate()),
    D: String(raw.getDate()),
    HH: pad(raw.getHours()),
    H: String(raw.getHours()),
    mm: pad(raw.getMinutes()),
    m: String(raw.getMinutes()),
    ss: pad(raw.getSeconds()),
    s: String(raw.getSeconds()),
  };
  // 긴 토큰 우선 매칭(YYYY > ... > s) — 부분 잠식 방지.
  return pattern.replace(/YYYY|MM|M|DD|D|HH|H|mm|m|ss|s/g, (t) => map[t] ?? t);
}

/**
 * 파일 URL normalize — host 치환(범용/순수). host 미주입 시 passthrough(빈값 가드만).
 *
 * fromHost(기본 'http://localhost:3000')를 toHost로 치환한다. toHost가 없으면 원 URL 그대로.
 * 도메인 특정 상수에 의존하지 않도록 host는 인자로 받는다.
 */
function normalizeUrl(value: unknown, toHost?: string, fromHost = 'http://localhost:3000'): string {
  const url = toText(value);
  if (!url) return '';
  if (toHost && url.includes(fromHost)) return url.replace(fromHost, toHost);
  return url;
}

/**
 * 허용 색 형식 — CSS 인젝션 방지용 화이트리스트.
 * hex(#rgb/#rgba/#rrggbb/#rrggbbaa) 또는 rgb()/rgba()(숫자·%·공백·쉼표·점·슬래시만).
 * `;`/`url(`/`expression` 등 위험 토큰이 섞이면 매칭 실패 → 무시된다.
 */
const CSS_COLOR_RE = /^(#[0-9a-fA-F]{3,8}|rgba?\([0-9.,%\s/]+\))$/;

/** hex(#rgb/#rgba/#rrggbb/#rrggbbaa) → rgba(r,g,b,alpha). 3/4자리는 각 자릿수를 배로 확장.
 *  rc 안 hex→rgba 변환의 정본 — AnnotatedImageView(채움)·RiskCriteriaDisplay(셀 tint)가 공유. */
export function hexToRgba(hex: string, alpha: number): string {
  let h = hex.slice(1);
  if (h.length === 3 || h.length === 4) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * 셀 배경 데이터 바인딩 색 해석 — RenderData(scope)에서 backgroundKey로 색 문자열을 읽어
 * 검증(#hex/rgb/rgba 화이트리스트, CSS 인젝션 방지) 후 CSS 색 문자열을 반환한다(범용).
 *
 * - 값이 없거나(빈값·undefined) 색 형식이 아니면 **undefined** 반환 → 호출부는 정적 배경 유지.
 * - alpha(0~1, 기본 1)가 1 미만이면 hex를 rgba(r,g,b,alpha)로 변환(hex 외 형식은 원문 그대로).
 *   alpha는 [0,1]로 클램프하며, 유한수가 아니면 1로 본다.
 *
 * scope는 RenderData flat(예: 'levelCriteriaArr.0.color') 기준으로 getByPath 조회한다.
 */
export function resolveCellBackgroundColor(
  scope: unknown,
  key: string,
  alpha?: number,
): string | undefined {
  if (!key) return undefined;
  const raw = getByPath(scope, key);
  if (typeof raw !== 'string') return undefined;
  const color = raw.trim();
  if (!CSS_COLOR_RE.test(color)) return undefined;
  const a = typeof alpha === 'number' && Number.isFinite(alpha) ? Math.min(1, Math.max(0, alpha)) : 1;
  if (a >= 1) return color;
  return color.startsWith('#') ? hexToRgba(color, a) : color;
}

/**
 * '{...}' 치환 — '{value}'는 입력값, '{a.b}'는 scope 경로 조회. 미해결 경로는 ''로 치환.
 */
function applyTemplate(value: unknown, template: string, scope: unknown): string {
  // 저작자 이스케이프 `\n`(백슬래시+n 두 글자) → 실제 개행. 렌더는 pre-wrap(셀·텍스트)이라
  // 개행 문자가 곧 줄바꿈으로 표시된다. 예: '{value}~{a.max}\n({a.label})' → 2줄.
  // template/join itemTemplate/switch cases가 모두 이 함수를 경유하므로 일괄 적용.
  return template.replace(/\\n/g, '\n').replace(/\{([^}]+)\}/g, (_, expr: string) => {
    const key = expr.trim();
    if (key === 'value') return toText(value);
    return toText(getByPath(scope, key));
  });
}

/**
 * 템플릿이 참조하는 placeholder({value} + scope 경로)가 **하나 이상 있고 전부 빈값**인지 판정.
 *
 * template/switch kind의 `empty`(opt-in placeholder) 가드가 공유한다 — 참조가 전부 결측이면
 * 정적 잔재('{a}x{b}' → 'x', '{value}명' → '명')만 남으므로 통째 empty로 대체할 신호.
 * placeholder가 아예 없는 정적 템플릿(keys.length === 0)은 결측 개념이 없으므로 false(미개입).
 */
function allPlaceholdersEmpty(template: string, value: unknown, scope: unknown): boolean {
  const keys = [...template.matchAll(/\{([^}]+)\}/g)].map((m) => m[1].trim());
  return (
    keys.length > 0 &&
    keys.every((k) => (k === 'value' ? toText(value) === '' : toText(getByPath(scope, k)) === ''))
  );
}

/**
 * 플래그(체크) truthy 판정 — checkbox 위젯 저장값(boolean) + map 관례('true' 문자열) 둘 다 참으로 본다.
 * 그 외(false·null·undefined·''·0·'yes' 등)는 미체크. applyFormat 'bool'과 CheckboxNode 렌더가 공유.
 */
export function isFlagTruthy(value: unknown): boolean {
  return value === true || value === 'true';
}

/**
 * rangeMap `rangesFrom`(구간 배열의 **선언 출처**) 해소 — 카탈로그 entry에서 구간 배열을 꺼낸다.
 *
 * ⚠️ rc는 API를 모른다 — 호스트가 이미 resolve해 스코프에 실어 둔 채널에서만 읽는다. 채널 규약은
 * optionsFrom(select 동적 옵션)의 `resolveDynamicSelectRows`와 **동형**이다: 카탈로그 키로 스코프를
 * 조회하되, 호스트가 주입한 rows(resolvedRows)든 값 채널(values/RenderData)이든 **같은 키**로 실린다.
 *  - 본문 렌더: scope = RenderData flat (서버 prefill이 실은 단수 entry 객체).
 *  - 작성폼 렌더: scope = writeFormatScope 합성({...outerValues, ...values, ...resolvedRows}) —
 *    resolvedRows가 맨 위라 host 주입본이 자동으로 우선이다(그쪽 doc 규약).
 * 단수(single) 카탈로그 entry는 resolvedRows 채널에서 **배열로 감싸져** 올 수 있으므로 첫 항목으로
 * 방어한다(RiskEstimationWidget `extractRiskEstimationCriteria`와 같은 방어).
 * select 생략 시엔 그 카탈로그 키 자체가 구간 배열이라는 뜻.
 * 어느 채널에도 없으면 undefined → 호출부가 rangesKey/ranges로 폴백하고, 그마저 없으면 미매칭
 * (배지 없이 텍스트로 degrade — 가짜 라벨·가짜 색 금지).
 */
function resolveRangesFrom(
  from: { catalogKey: string; select?: string },
  scope: unknown,
): unknown[] | undefined {
  // 해소 규약은 scopeFrom과 **한 구현**(resolveCatalogScopeValue)이다 — 구간 배열은 그 산출이
  // 배열일 때만 채택한다(빈 배열도 여기선 통과 — 폴백 판정은 호출부 matchRangeCriterion 소유).
  const picked = resolveCatalogScopeValue(from, scope);
  return Array.isArray(picked) ? picked : undefined;
}

/**
 * 카탈로그 채널에서 값 하나를 꺼내는 **해소 규약의 단일 구현** — `rangesFrom`(구간 배열)과
 * `scopeFrom`(보조 자료 스코프 주입)이 같은 규약을 공유한다.
 *
 * ⚠️ rc는 API를 모른다 — 호스트가 이미 resolve해 스코프에 실어 둔 채널에서만 읽는다(위 doc).
 *  - `select` 미지정 = 그 카탈로그 키의 값 자체.
 *  - 단수 entry가 resolvedRows 채널에서 **배열로 감싸져** 올 수 있어 select 경로는 첫 항목에서 읽는다.
 *  - 어느 채널에도 없거나 경로가 비면 undefined — 호출부가 "얹지 않음"(scopeFrom) 또는
 *    "다음 축으로"(rangesFrom)로 처리한다.
 */
function resolveCatalogScopeValue(
  from: { catalogKey: string; select?: string },
  scope: unknown,
): unknown {
  if (!scope || typeof scope !== 'object') return undefined;
  const raw = (scope as Record<string, unknown>)[from.catalogKey];
  if (raw === undefined || raw === null) return undefined;
  if (!from.select) return raw;
  const entry = Array.isArray(raw) ? raw[0] : raw;
  if (!entry || typeof entry !== 'object') return undefined;
  return getByPath(entry as Record<string, unknown>, from.select);
}

/**
 * 형식이 선언한 보조 자료(`format.scopeFrom`)를 스코프에 얹은 **사본**을 돌려준다 —
 * 형식이 스코프를 읽기 **직전**에 통과하는 단 하나의 문이다.
 *
 * 형식의 경로 표현식(unitPath / `{a.b}` placeholder / switch.on / rangesKey / annotationKey)은
 * 그대로 두고, 그 경로가 가리키는 이름을 여기서 채운다 — 그래서 캐리어(히든 위젯·branch
 * conditionBinding)로 미리 적재하던 값을 **표현식 수정 없이** 형식 자신의 선언으로 옮길 수 있다
 * (결정 2026-08-07 §C).
 *
 * 규약:
 *  - **선언이 없거나 비면 원본 스코프를 그대로** 돌려준다(새 객체를 만들지 않는다 — 참조 동일성이
 *    바뀌면 memo/렌더 비교가 헛돈다. 회귀 0의 조건이기도 하다).
 *  - 해소 실패(undefined)면 **그 키를 얹지 않는다** — 스코프에 이미 있는 값을 undefined로 덮어
 *    멀쩡히 보이던 표시를 죽이지 않기 위함이다(가짜 빈칸 금지).
 *  - **멱등하다** — 이미 얹힌 스코프에 다시 적용해도 같은 결과다(선언은 `catalogKey`(원본 채널)를
 *    읽지 얹힌 `key`를 읽지 않는다). 그래서 applyFormat 입구와 matchRangeCriterion/
 *    resolveRangeAnnotation 입구에 **모두** 두어도 안전하다 — 배지 렌더(rangeBadge)처럼
 *    applyFormat을 거치지 않고 판정 함수를 직접 부르는 경로가 있어 양쪽 다 필요하다.
 *    (`key === catalogKey`인 자기참조 선언만 2회차 해소가 실패하는데, 그때도 1회차 결과가
 *     그대로 남아 결과는 같다.)
 */
export function withFormatScope(format: BindingFormat | undefined, scope: unknown): unknown {
  const sources = format?.scopeFrom;
  if (!sources || sources.length === 0) return scope;
  if (!scope || typeof scope !== 'object' || Array.isArray(scope)) return scope;
  let overlaid: Record<string, unknown> | undefined;
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    const { key, catalogKey } = source;
    if (typeof key !== 'string' || key === '') continue;
    // 프로토타입 오염 방지: overlaid[key] 직접 대입 대상이므로 위험 키는 얹지 않는다(getByPath 관례).
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') continue;
    if (typeof catalogKey !== 'string' || catalogKey === '') continue;
    const value = resolveCatalogScopeValue(source, scope);
    if (value === undefined) continue;
    // 얹을 게 실제로 생겼을 때만 사본을 만든다(위 규약).
    if (!overlaid) overlaid = { ...(scope as Record<string, unknown>) };
    overlaid[key] = value;
  }
  return overlaid ?? scope;
}

/**
 * rangeMap 구간 판정의 **단일 정본** — 숫자 값이 어느 구간(range)에 드는지 찾아 그 구간 객체를
 * 그대로 돌려준다. 매칭 실패(비숫자/빈값/구간 미주입/밴드 밖)는 undefined.
 *
 * 구간 배열 해소 우선순위: **rangesFrom(선언 출처 — 카탈로그) → rangesKey(스코프 경로) →
 * ranges(정적 fallback)**. 앞의 축이 스코프에서 해소되지 않으면 다음 축으로 내려간다.
 * 반환 객체의 `color`는 **동적 배열에만 있을 수 있는 값**이다(정적 ranges 타입엔 색이 없다 —
 * 색은 저작 대상이 아니라 기준표 소유. BindingFormat JSDoc 참조). 문자열 표시는 applyFormat이,
 * 색을 쓰는 비주얼(배지)은 render 1회 지점이 이 함수의 같은 판정 결과로 그린다 —
 * applyFormat이 색을 문자열에 섞지 않게 하려는 분리다(bool display:'box'와 동형).
 */
export function matchRangeCriterion(
  value: unknown,
  format: Extract<BindingFormat, { kind: 'rangeMap' }>,
  rawScope?: unknown,
): { min: number; max: number; label: string; color?: string } | undefined {
  if (isEmptyValue(value)) return undefined;
  const num = Number(value);
  if (!Number.isFinite(num)) return undefined;
  // 형식이 선언한 보조 자료를 얹은 뒤 읽는다 — 배지 렌더(rangeBadge)는 applyFormat을 거치지 않고
  // 이 함수를 직접 부르므로, 여기서 얹지 않으면 **배지만 새 축을 못 본다**(멱등하므로 중복 무해).
  const scope = withFormatScope(format, rawScope);
  let ranges: Array<{ min: number; max: number; label: string; color?: string }> | undefined;
  // ⚠️ 각 축은 **비지 않은 배열**일 때만 "해소됨"으로 본다. 빈 배열([])도 truthy라 그냥 채택하면
  // 뒤 축(rangesKey → 정적 ranges)이 통째로 막혀, 배지·라벨 없이 숫자만 나오는 조용한 degrade가 된다
  // (riskLevelCriteria는 non-null Json이라 []가 실제로 실려 온다).
  if (format.rangesFrom) {
    const declared = resolveRangesFrom(format.rangesFrom, scope);
    if (Array.isArray(declared) && declared.length > 0) {
      ranges = declared as Array<{ min: number; max: number; label: string; color?: string }>;
    }
  }
  if (!ranges && format.rangesKey) {
    const dyn = getByPath(scope, format.rangesKey);
    if (Array.isArray(dyn) && dyn.length > 0) {
      ranges = dyn as Array<{ min: number; max: number; label: string; color?: string }>;
    }
  }
  if (!ranges && Array.isArray(format.ranges) && format.ranges.length > 0) ranges = format.ranges;
  // 구간 미주입/빈 배열 → 판정 없음(가짜 라벨·가짜 색 금지).
  if (!ranges || ranges.length === 0) return undefined;
  return ranges.find((r) => {
    const min = Number(r?.min);
    const max = Number(r?.max);
    return Number.isFinite(min) && Number.isFinite(max) && num >= min && num <= max;
  });
}

/**
 * rangeMap `annotationKey`(병기 값) 해소 — 표시 옆에 나란히 둘 값의 문자열.
 *
 * **rangesKey와 같은 스코프·같은 읽기**(getByPath)다 — 본문은 RenderData flat, 작성폼은
 * writeFormatScope 합성, 반복 안에서는 행 스코프. 병기용 파이프를 따로 만들지 않는다.
 * 비었으면(경로 없음/빈 문자열/공백뿐) 빈 문자열을 돌려 호출부가 **아무것도 그리지 않게** 한다
 * (빈 span·여분 공백도 남기지 않는다 — 배지만 있는 칸과 같은 모양이어야 한다).
 * 앞뒤 공백은 떼고 돌려준다(간격은 렌더가 소유 — 값에 섞인 공백이 간격을 흔들지 않게).
 */
export function resolveRangeAnnotation(
  format: Extract<BindingFormat, { kind: 'rangeMap' }>,
  rawScope?: unknown,
): string {
  if (!format.annotationKey) return '';
  // 병기 값도 형식이 선언한 보조 자료를 볼 수 있어야 한다 — 배지 렌더가 applyFormat 밖에서
  // 이 함수를 직접 부르므로 여기서도 얹는다(멱등).
  const raw = getByPath(withFormatScope(format, rawScope), format.annotationKey);
  if (isEmptyValue(raw)) return '';
  return toText(raw).trim();
}

/**
 * join 형식의 **구분 그룹 결합**(format.groupBy 지정 시) — 객체 항목을 그 필드 값별로 묶어
 * "그룹 머리글 + 그룹 항목들"로 낸다. 표 위젯 groupByField와 같은 어휘·같은 의미(표시 축).
 *
 * - 그룹 순서 = 첫 등장 순, 그룹 안 순서 = 원 배열 순.
 * - 구분 값이 빈 항목(객체가 아닌 항목 포함)은 묶지 않고 그 자리에 항목 텍스트만 단독으로 낸다
 *   — 옛 형태(string[])로 저장된 값이 가짜 머리글을 얻지 않고 종전과 같은 나열로 degrade한다.
 * - 항목 텍스트가 빈 항목은 버린다(작성 중 추가만 하고 비워 둔 행이 머리글만 남기지 않게).
 */
function joinGrouped(
  items: unknown[],
  format: { groupBy?: string; groupTemplate?: string; groupSeparator?: string },
  separator: string,
  itemText: (item: unknown, index: number) => string,
): string {
  type Entry = { kind: 'group'; group: string; texts: string[] } | { kind: 'plain'; text: string };
  const entries: Entry[] = [];
  const indexByGroup = new Map<string, number>();

  items.forEach((item, i) => {
    const text = itemText(item, i);
    if (text.trim() === '') return;
    const isObject = item !== null && typeof item === 'object' && !Array.isArray(item);
    const group = isObject
      ? toText(getByPath(item, format.groupBy as string)).trim()
      : '';
    if (group === '') {
      entries.push({ kind: 'plain', text });
      return;
    }
    const idx = indexByGroup.get(group);
    if (idx !== undefined) {
      (entries[idx] as { kind: 'group'; group: string; texts: string[] }).texts.push(text);
    } else {
      indexByGroup.set(group, entries.length);
      entries.push({ kind: 'group', group, texts: [text] });
    }
  });

  const groupTpl = format.groupTemplate ?? `{group}${separator}{items}`;
  return entries
    .map((e) => {
      if (e.kind === 'plain') return e.text;
      const items = e.texts.join(separator);
      // 다른 템플릿 축(template/join itemTemplate/switch cases = applyTemplate)과 **같은 규약**:
      //  (1) 저작자 이스케이프 `\n`(백슬래시+n 두 글자) → 실제 개행.
      //  (2) 치환은 반드시 **함수**로. 문자열 치환은 치환값 안의 `$&`·`` $` ``·`$'`를 특수 패턴으로
      //      해석해(조문 본문 등 실데이터에 존재할 수 있다) 내용을 중복·유실시킨다.
      return groupTpl
        .replace(/\\n/g, '\n')
        .replace(/\{(group|items)\}/g, (_, key: string) => (key === 'group' ? e.group : items));
    })
    .join(format.groupSeparator ?? separator);
}

/**
 * 범용 표시 포맷을 적용한다(render 후처리). scope는 unitPath/template 보조 경로 조회용.
 *
 * format이 없으면 원값을 그대로 반환한다(회귀 0). kind별로 도메인 무관 연산만 수행한다.
 *
 * 형식이 보조 자료를 선언했으면(`scopeFrom`) **여기 입구에서 1회** 스코프에 얹고, 이후 모든 kind가
 * 그 스코프를 본다 — kind마다 따로 해소하면 같은 규약이 여러 벌이 된다(withFormatScope doc).
 */
export function applyFormat(value: unknown, format: BindingFormat | undefined, rawScope?: unknown): unknown {
  if (!format) return value;
  const scope = withFormatScope(format, rawScope);

  switch (format.kind) {
    case 'unit': {
      if (isEmptyValue(value)) return format.empty ?? '';
      const unit = format.unitPath ? toText(getByPath(scope, format.unitPath)) : (format.unit ?? '');
      return unit ? `${toText(value)} ${unit}` : toText(value);
    }
    case 'date': {
      const pattern = format.pattern ?? DATE_DISPLAY_PATTERN_DEFAULT;
      // 기간 객체({start,end})도 같은 kind가 받는다 — 각 끝에 **같은 pattern**을 적용해 ' ~ '로
      // 잇는다(한쪽 결측이면 그쪽만 비운다). 기간에 별도 kind를 두지 않는 이유는 shared
      // BindingFormat doc 참조: 저작자가 고르는 건 "날짜 표기" 하나고, 단일/기간은 값의 형태 차이다.
      // 문자열 값은 이 분기를 타지 않는다(isDateRangeValue가 객체만 인정) — 기존 동작 그대로.
      if (isDateRangeValue(value)) {
        const text = formatDateRange(value, (end) => (isEmptyValue(end) ? '' : formatDate(end, pattern)));
        // 양끝 모두 빈 기간은 "빈값"과 같은 취급(위 empty 의미론 승계).
        return text === '' ? (format.empty ?? '') : text;
      }
      if (isEmptyValue(value)) return format.empty ?? '';
      return formatDate(value, pattern);
    }
    case 'url':
      // host 주입은 호출부가 scope에 '__fileHost'로 넣어주면 치환, 없으면 passthrough(순수).
      return normalizeUrl(value, getByPath(scope, '__fileHost') as string | undefined);
    case 'template':
      // empty **정의됨** + 템플릿이 참조하는 placeholder({value}·scope 경로)가 전부 빈값
      // → 통째 empty로 대체(저작자 명시 시에만). switch kind의 empty 가드와 동일 의미론이라
      // '{a.b}/{c.d}'처럼 {value} 미참조 템플릿도 전부 결측이면 '/' 잔재 대신 empty가 된다.
      // empty 미정의 기본은 pass-through — '{value}'를 ''로 치환해 정적 텍스트는 유지
      // (예: '측정 장비명 ( {value} )' → '측정 장비명 (  )'. 접미어만 남는 게 싫으면 empty를 지정).
      if (format.empty !== undefined && allPlaceholdersEmpty(format.template, value, scope)) {
        return format.empty;
      }
      return applyTemplate(value, format.template, scope);
    case 'default':
      return isEmptyValue(value) ? format.fallback : value;
    case 'join': {
      if (!Array.isArray(value) || value.length === 0) return format.empty ?? '';
      const sep = format.separator ?? '\n';
      // plainWhenSingle: 항목 1개면 itemTemplate 미적용(값만) — 단건에 번호("1. …")가 붙는 것 방지
      // (구 SMS 감소대책 동형). 미지정=항상 itemTemplate 적용(기존 동작, 회귀 0).
      const skipTemplate = format.plainWhenSingle === true && value.length === 1;
      const itemText = (item: unknown, i: number): string => {
        // 객체가 아닌 항목은 템플릿의 {field}를 읽을 수 없다 — 값 그대로(옛 형태 string[] degrade).
        const isObject = item !== null && typeof item === 'object' && !Array.isArray(item);
        if (!format.itemTemplate || skipTemplate || (format.groupBy !== undefined && !isObject)) {
          return toText(item);
        }
        // 항목 템플릿: {value}=항목(원시), {field}=항목.field(객체), {n}=1-based 순번.
        // {n}을 먼저 치환한 뒤 applyTemplate(scope=item)로 {value}/{field} 치환.
        const tpl = format.itemTemplate.replace(/\{n\}/g, String(i + 1));
        return applyTemplate(item, tpl, item);
      };
      if (format.groupBy === undefined) return value.map(itemText).join(sep);
      return joinGrouped(value, format, sep, itemText);
    }
    case 'map': {
      const key = toText(value);
      return Object.prototype.hasOwnProperty.call(format.cases, key)
        ? format.cases[key]
        : (format.fallback ?? key);
    }
    case 'switch': {
      const sel = toText(getByPath(scope, format.on));
      const matched = Object.prototype.hasOwnProperty.call(format.cases, sel);
      const tpl = matched ? format.cases[sel] : (format.default ?? '');
      // empty 정의 시(미정의=기존 그대로, 회귀 0):
      //  (1) 케이스 미매칭 + default 미정의 → 표시할 템플릿 자체가 없음 → empty
      //      (default를 ''로 **명시**한 건 저작자 의도이므로 그대로 빈 문자열).
      //  (2) 선택된 템플릿의 placeholder가 하나 이상 있고 전부 빈값 해석 → 통째 empty
      //      ('{a}x{b}'가 둘 다 결측 → 'x' 잔재 방지).
      if (format.empty !== undefined) {
        if (!matched && format.default === undefined) return format.empty;
        if (allPlaceholdersEmpty(tpl, value, scope)) return format.empty;
      }
      return applyTemplate(value, tpl, scope);
    }
    case 'rangeMap': {
      // 숫자 값을 구간 배열로 판정해 라벨 표시(도메인 무관 선언 도구).
      // 비숫자(빈값 포함)는 pass-through(원값 그대로) — 라벨 판정 대상 아님.
      if (isEmptyValue(value)) return value;
      if (!Number.isFinite(Number(value))) return value;
      // 판정은 matchRangeCriterion 단일 정본(배지 렌더와 같은 결과를 공유).
      // 구간 미주입/매칭 없음 → 값만 표시(가짜 라벨 금지).
      const matched = matchRangeCriterion(value, format, scope);
      if (!matched) return toText(value);
      // ⚠️ display('badge')는 **문자 변환에 관여하지 않는다** — 배지든 텍스트든 같은 문자열을 내고,
      // 알약 비주얼과 색은 render 1회 지점이 같은 판정 결과로 그린다. 덕분에 배경색이 빠지는
      // 인쇄 환경/배지 미지원 표시 경로에서도 라벨이 그대로 남는다.
      const label = toText(matched.label);
      if ((format.output ?? 'value-label') === 'label') return label;
      return label ? `${toText(value)} (${label})` : toText(value);
    }
    case 'bool': {
      // display='box'는 render(체크박스 비주얼)가 그리므로 문자 변환에 관여하지 않는다(빈 문자열 반환).
      if (format.display === 'box') return '';
      // boolean → 표시 문자. 빈값(false·null·undefined·'')은 미체크로 falseText(기본 '')를 반환.
      return isFlagTruthy(value) ? (format.trueText ?? 'V') : (format.falseText ?? '');
    }
    default:
      return value;
  }
}

/**
 * 단일 binding을 render 모드로 resolve한다(C3 듀얼 수용, Phase D live 수용).
 *
 * - lifecycle 'live' : liveData가 있으면 거기서 현재값을 읽고(getByPath(liveData,
 *   select ?? bindingKey) — snapshot과 동일 경로), 없으면 undefined(하위호환). 그 외 모든
 *   분기보다 우선. liveData 경로도 format 후처리는 동일하게 적용된다.
 * - **catalogKey 있으면(카탈로그 경로 우선)**: render는 어차피 content에 박제된 값을 읽으므로
 *   getByPath(content, binding.select ?? bindingKey). (catalog 미주입이어도 동작 — path 읽기뿐)
 * - 없으면 레거시:
 *   - source 'const' : binding.constValue
 *   - source 'system'/'ai'/'input' : getByPath(content, api.select ?? bindingKey)
 *
 * select 경로가 없으면 bindingKey 자체를 content 키로 본다(단순 키 호환).
 * catalog 인자는 render에서 사실상 불필요하나(읽기 경로뿐) compose와 시그니처 정합성을 위해 받는다.
 * liveData는 optional 마지막 인자라 기존 2~3-arg 호출은 그대로 동작한다(하위호환).
 *
 * binding.format이 있으면 추출 원값에 applyFormat을 적용한 표시값을 반환한다(render 후처리 전용).
 * scope=content로 unitPath/template 보조 경로를 조회한다.
 */
export function resolveBinding(
  binding: Binding,
  content: Record<string, unknown>,
  _catalog?: CatalogIndex,
  liveData?: Record<string, unknown>,
): unknown {
  // live + liveData 미주입: 종전과 동일하게 undefined(format도 미적용 — 하위호환).
  if (binding.lifecycle === 'live' && !liveData) return undefined;

  const raw = resolveBindingRawWithFallback(binding, content, liveData);
  return applyFormat(raw, binding.format, content);
}

/**
 * binding 원값 + 하위호환 폴백 — `bindingKey`가 스코프에 아예 없을 때만 `fallbackKey`로 재시도.
 * 규약(빈 배열은 폴백 안 함 등) 정본은 shared `Binding.fallbackKey` doc.
 */
function resolveBindingRawWithFallback(
  binding: Binding,
  content: Record<string, unknown>,
  liveData?: Record<string, unknown>,
): unknown {
  const raw = resolveBindingRaw(binding, content, liveData);
  if (raw !== undefined || !binding.fallbackKey) return raw;
  return resolveBindingRaw(
    { ...binding, bindingKey: binding.fallbackKey, select: undefined },
    content,
    liveData,
  );
}

/** binding의 원값만 추출(format 미적용). resolveBinding이 format 후처리 전 단계로 호출. */
function resolveBindingRaw(
  binding: Binding,
  content: Record<string, unknown>,
  liveData?: Record<string, unknown>,
): unknown {
  // 'live'는 BindingSource가 아닌 lifecycle 값 — 렌더 시 재조회 대상.
  // liveData(현재값)가 주입되면 snapshot과 **동일한 읽기 경로**로 읽고(catalogKey=select??bindingKey,
  // 레거시=api.select??bindingKey), 없으면 undefined(하위호환 — liveData 미전달 호출은 종전대로 미렌더).
  if (binding.lifecycle === 'live') {
    if (!liveData) return undefined;
    const livePath = binding.catalogKey
      ? binding.select ?? binding.bindingKey
      : binding.api?.select ?? binding.bindingKey;
    return getByPath(liveData, livePath);
  }

  // 카탈로그 경로 우선: render는 박제값 읽기뿐 → snapshotKey(조건 select면 bindingKey) 경로로 추출.
  if (binding.catalogKey) {
    const baked = getByPath(content, snapshotKey(binding));
    if (baked !== undefined) return baked;
    // 박제 전 폴백(2026-07-21): 미리보기(compose 미경유 — resolveForRender(template, values) 직행)나
    // 박제 누락 저장본에서, content(값 채널)에 카탈로그 **원천 객체**(예 prefill 주입
    // 'risk-assessment-config')가 실려 있으면 select 경로로 직접 읽는다 — 저장본은 compose가
    // snapshotKey에 박제하므로 이 폴백을 타지 않는다(회귀 0). 조건 구문 select(`[key=...]`)는
    // getByPath가 못 읽으니 제외, 배열(리스트 카탈로그)도 제외(단건 객체 원천만).
    if (binding.select && !binding.select.includes('[')) {
      const origin = content[binding.catalogKey];
      if (origin && typeof origin === 'object' && !Array.isArray(origin)) {
        const fromOrigin = getByPath(origin as Record<string, unknown>, binding.select);
        if (fromOrigin !== undefined) return fromOrigin;
      }
    }
    // bindingKey 폴백(2026-07-21): 시스템 catalog 바인딩(예 헤더 사업장명 cell-0-1→organization-site
    // .site.name / 점검일자 cell-1-1→inspection-with-findings.startedAt)을 **작성폼에서 편집**하면
    // 입력값은 bindingKey(cell-0-1)에 저장되나 render는 snapshotKey(=select)로 읽어 미스매치 →
    // 미리보기에서 빈칸이 된다. snapshotKey≠bindingKey일 때만 content[bindingKey]를 최후로 시도한다
    // (저장본은 compose가 snapshotKey에 박제하므로 위 baked에서 이미 반환 — 회귀 0).
    if (snapshotKey(binding) !== binding.bindingKey) {
      // 합성/파생 bindingKey는 점을 포함해도 prefill이 **flat 키**(result[bindingKey])로 저장한다
      //   (예 site-accident-stats.1.injuryCount-r3c6=2 — 리스트 카탈로그 셀). getByPath는 점을
      //   분해해 이 flat 키를 놓치므로(content["site-accident-stats"]["1"]["injuryCount-r3c6"] 탐색)
      //   미리보기에서 빈칸이 된다. flat 조회를 우선하고 없을 때만 getByPath 폴백(2026-07-21 재해발생
      //   현황 미리보기 빈칸 fix). 점 없는 합성 키(cell-0-1 등)는 flat=getByPath 동일 → 회귀 0.
      //   저장본은 compose가 snapshotKey에 박제하므로 위 baked에서 이미 반환(이 경로 미도달).
      const flat = content[binding.bindingKey];
      if (flat !== undefined) return flat;
      const byBindingKey = getByPath(content, binding.bindingKey);
      if (byBindingKey !== undefined) return byBindingKey;
    }
    return baked;
  }

  // 레거시 경로(source 기반)
  switch (binding.source) {
    case 'const':
      return binding.constValue;
    case 'system':
    case 'ai':
    case 'input':
    default:
      return getByPath(content, binding.api?.select ?? binding.bindingKey);
  }
}

/**
 * out[key]에 resolve 값을 기록하되, **이미 정의된 값을 undefined로 덮지 않는다.**
 *
 * walkNode는 repeat 템플릿 셀 바인딩도 부모 content 기준으로 resolve하는데(항목 필드명이라
 * 보통 undefined), 그 필드명이 dataDef/dataSource 키와 같으면 seed된 행 배열이 undefined로
 * 덮여 반복 전개(items)가 빈 배열이 된다(데이터셋 키=칼럼 필드명 중복 시 반복 미출력 버그).
 * 미존재 키의 own undefined 사전 적재(readBindingValue fallback 규약)는 그대로 유지한다.
 */
function assignResolved(out: RenderData, key: string, value: unknown): void {
  if (value === undefined && out[key] !== undefined) return;
  out[key] = value;
}

/**
 * 노드 1개의 binding을 out 맵에 **raw(format 미적용)** 로 기록한다(binding 없으면 skip).
 *
 * 표 셀 binding과 동일 원칙 — resolve는 원값만 적재하고 format은 render(RenderTemplate)에서 1회
 * 적용한다. 이래야 placeholder 게이트(빈값 판정)가 fallback/empty 적용 *전* raw를 보고 판단해,
 * 바인딩 보기에서 fallback이 placeholder를 가리지 않는다(비표 노드 resolve=raw / render=format 정렬).
 */
function resolveNodeBinding(
  node: ComposerNode,
  content: Record<string, unknown>,
  out: RenderData,
  _catalog?: CatalogIndex,
  liveData?: Record<string, unknown>,
): void {
  if (node.binding) {
    assignResolved(
      out,
      node.binding.bindingKey,
      resolveBindingRawWithFallback(node.binding, content, liveData),
    );
  }
}

/** 노드 트리를 재귀 순회하며 모든 binding을 resolve한다(group/repeat/table 셀 content 포함). */
function walkNode(
  node: ComposerNode,
  content: Record<string, unknown>,
  out: RenderData,
  catalog?: CatalogIndex,
  liveData?: Record<string, unknown>,
): void {
  resolveNodeBinding(node, content, out, catalog, liveData);

  switch (node.kind) {
    case 'group':
    case 'stack':
      for (const child of node.children) walkNode(child, content, out, catalog, liveData);
      break;
    case 'repeat':
      // repeat.template(GroupNode)도 순회 — 단, 반복 항목 scope는 transform이 채우므로
      // 여기서 resolve되는 셀 binding은 부모 content 기준(보통 항목 키는 없어 undefined).
      walkNode(node.template, content, out, catalog, liveData);
      break;
    case 'branch':
      // 조건 데이터 바인딩을 RenderData[key]에 raw로 적재한다(format 미적용) — evaluateCondition이
      // 이 key를 읽어 슬롯 condition을 평가한다. cell/노드 binding과 달리 적재 키는 bindingKey가
      // 아니라 cb.key(조건 식별 키)다(캐리어 노드 트릭 대체 — branch가 직접 비교 대상 보유).
      for (const cb of node.conditionBindings ?? []) {
        assignResolved(out, cb.key, resolveBindingRaw(cb.binding, content, liveData));
      }
      // 모든 슬롯 children의 binding을 resolve한다(렌더는 첫 매칭 슬롯만이나, 어느 슬롯이든
      // 조건에 따라 렌더될 수 있어 전 슬롯을 미리 resolve해 둔다 — group.children와 동일 취급).
      for (const slot of node.branches) {
        for (const child of slot.children) walkNode(child, content, out, catalog, liveData);
      }
      break;
    case 'table':
      for (const cell of node.cells) {
        if (cell.binding) {
          // 셀 binding은 RenderData에 **raw(format 미적용)** 로 저장한다.
          // renderTableCell이 렌더 시 applyFormat을 1회 적용하므로(repeat 항목 scope와 균일),
          // 여기서 format을 적용하면 표시 시 이중 적용된다(예: unit '명' → "52 명 명").
          // (비표 노드 binding은 RenderData 값이 primitive로 재포맷되지 않아 resolveBinding 유지가 맞다.)
          assignResolved(
            out,
            cell.binding.bindingKey,
            resolveBindingRawWithFallback(cell.binding, content, liveData),
          );
        }
        if (cell.compute) {
          // 계산 셀 입력 바인딩을 RenderData에 raw(format 미적용)로 적재한다 — 렌더 시
          // computeCellCompute가 bindingKey로 scope에서 읽고 최종 format을 1회 적용한다.
          // (표 셀 binding과 동일 raw 원칙. const 입력은 박을 값이 없어 skip.)
          for (const inp of cell.compute.inputs) {
            if (inp.binding) {
              assignResolved(out, inp.binding.bindingKey, resolveBindingRaw(inp.binding, content, liveData));
            }
          }
        }
        if (cell.content) {
          for (const inner of cell.content) walkNode(inner, content, out, catalog, liveData);
        }
      }
      break;
    case 'image':
      // 오버레이 작성 토글 seed — 이미지 노드는 binding 외에 축별 **규약 콘텐츠 키**
      // (key ?? OVERLAY_SHAPES_CONTENT_KEY / strokesKey ?? OVERLAY_STROKES_CONTENT_KEY)를 렌더
      // 시 readBindingValue(data, 키)로 읽는다(RenderTemplate 오버레이 판정). 그 키는 body
      // 바인딩이 아니라 작성폼 전용 규약 키라 walkNode가 적재하지 않아, content(작성 저장값)에
      // 있어도 RenderData에 안 실려 토글이 실렌더에서 무효가 된다 → 여기서 content 값을 seed.
      // 같은 키의 body 바인딩/dataDef가 이미 채웠으면 보존(미채움일 때만). template.variables
      // 우회는 금지 — 변수로 선언하면 admin 변수 위젯에 배관 키가 중복 노출된다.
      for (const overlayKey of [
        node.overlay?.key ?? OVERLAY_SHAPES_CONTENT_KEY,
        node.overlay?.strokesKey ?? OVERLAY_STROKES_CONTENT_KEY,
      ]) {
        if (out[overlayKey] === undefined && content[overlayKey] !== undefined) {
          out[overlayKey] = content[overlayKey];
        }
      }
      break;
    default:
      break;
  }
}

/**
 * 템플릿 전체를 render 모드로 resolve해 RenderData를 만든다.
 *
 * template.body의 모든 노드(중첩 group/repeat/table 셀 content 포함)를 재귀 순회하며
 * 각 binding을 처리한다. 단순 필드(content 경로 추출)만 채워지며, 가공 배열·계산필드는
 * 도메인 transform이 별도로 채운다(호출부에서 머지).
 *
 * catalog는 optional — 기존 2-arg 호출(`resolveForRender(template, content)`)이 그대로 동작한다.
 * (render는 박제값 읽기뿐이라 catalog가 없어도 카탈로그 바인딩이 정상 resolve된다.)
 *
 * liveData도 optional(마지막 인자) — live lifecycle 바인딩만 content(snapshot) 대신 liveData
 * (현재값)에서 읽는다. 미전달 시 live 바인딩은 종전대로 undefined(하위호환). snapshot 바인딩은
 * liveData 유무와 무관하게 항상 content에서 읽는다.
 */
export function resolveForRender(
  template: ReportTemplate,
  content: Record<string, unknown>,
  catalog?: CatalogIndex,
  liveData?: Record<string, unknown>,
): RenderData {
  const out: RenderData = {};
  // dataDef seed(N2-A + N5-2b compute): 각 def.key를 우선순위로 채운다.
  //   content[def.key](작성 편집/오버라이드/snapshot) > compute(입력 조립 assembler) > defaultRows.
  // 표 repeatRegion/RepeatNode의 over가 def.key를 가리키면 이 배열로 행이 펼쳐진다.
  for (const def of template.dataDefs ?? []) {
    // 우선순위: content[def.key](snapshot/편집) > compute(입력 조립) > defaultRows.
    // 단 compute 파생 dataDef의 content가 **빈 배열**이면 작성 탭 초기화 잔재로 보고 compute를 재실행
    // 한다(작성 탭이 repeat over 키를 []로 초기화해도 미리보기/상세가 조인을 재현 — 2026-07-21
    // current-status-joined 미리보기 0행 사고). 비어있지 않은 스냅샷은 그대로 우선(편집 박제 보존),
    // compute 없는 편집형 빈 배열도 그대로.
    const snap = content[def.key];
    const isEmptyComputeArr = def.compute && Array.isArray(snap) && snap.length === 0;
    if (snap !== undefined && !isEmptyComputeArr) {
      out[def.key] = snap;
      continue;
    }
    if (def.compute) {
      // compute.inputs(입력명 → content dotted-path)를 resolve해 assembler에 넘긴다.
      const resolvedInputs: Record<string, unknown> = {};
      for (const [name, path] of Object.entries(def.compute.inputs ?? {})) {
        resolvedInputs[name] = getByPath(content, path);
      }
      const assembled = runAssembler(def.compute, resolvedInputs);
      out[def.key] = assembled ?? def.defaultRows; // 미등록 assembler면 defaultRows fallback
      continue;
    }
    out[def.key] = def.defaultRows;
  }
  // 동적 데이터셋 선언(D1) flat 적재 — dataSources의 list 선언의 content[def.key](작성폼 편집본/
  // 생성 snapshot/서버 조립 행 배열)를 RenderData에 노출한다(표 repeatRegion/RepeatNode의
  // over=def.key가 소비). readonly list도 적재한다 — 표시 전용(작성폼 미노출) 조립 데이터를
  // 반복(over)이 소비하는 정당한 케이스(예: 이번 점검 이행상태 카드). single 선언만 기존
  // resolve 산출 경로(binding/getByPath 소비) 그대로. seed 원본(`${key}__seed`)은 렌더
  // 비대상이라 적재하지 않는다. def.key는 점(.) 없는 flat 키만 쓴다(합성 키 함정 방지 —
  // feedback-composer-synthetic-bindingkey-dots-flat-read).
  for (const ds of template.dataSources ?? []) {
    if (ds.cardinality !== 'list') continue;
    if (out[ds.key] !== undefined) continue;
    if (content[ds.key] !== undefined) out[ds.key] = content[ds.key];
  }
  for (const node of template.body) walkNode(node, content, out, catalog, liveData);
  // 양식 전역 변수(TemplateVariable) — body 바인딩이 아니라 전역 선언이라 walkNode가 키를 만들지 않는다.
  // content[key](작성 저장값) ?? default를 RenderData에 노출 → mergeRepeatScope가 반복 행 scope에도 병합돼
  // 노드 switch(on: key)/조건/바인딩이 key로 참조한다(결정: 2026-07-07-composer-template-variables).
  // 바인딩/dataDef/dataSource가 같은 key를 이미 채웠으면 보존(변수는 미채운 key만 공급).
  for (const v of template.variables ?? []) {
    if (out[v.key] !== undefined) continue;
    out[v.key] = content[v.key] !== undefined ? content[v.key] : v.default;
  }
  // 슬롯 서명 렌더 맵(P2) 통과 — 서버 resolveLiveForReport가 liveData에 실은 예약 키를
  // RenderData로 넘긴다. 바인딩 키가 아니라 walkNode가 만들지 않으며, mergeRepeatScope
  // (`{...parent, ...row}`)로 반복 행 scope에서도 읽힌다(서명 셀 렌더의 값 채널).
  // content에는 이 키가 살지 않는다(§3-2) — liveData에만 있을 때 통과(회귀 0).
  if (liveData && liveData[SLOT_SIGNATURES_CONTENT_KEY] !== undefined) {
    out[SLOT_SIGNATURES_CONTENT_KEY] = liveData[SLOT_SIGNATURES_CONTENT_KEY];
  }
  return out;
}

// ============================================================
// compose 모드 (작성 → snapshot content 생성)
// ============================================================

/**
 * compose 입력 소스 — source별 bindingKey → 값 맵.
 *
 * - input  : 사용자 작성폼 값
 * - system : 작성 시점에 사전 수집된 API 값(서버 책임, 여기선 이미 resolve된 값)
 * - ai     : 사전 생성된 AI 산출 값
 *
 * 'const'/'live'는 소스에 없다(const는 render가 binding.constValue를 직접 읽고,
 *  live는 render 시 재조회 — 둘 다 content에 박지 않는다).
 */
export interface ComposeSources {
  input?: Record<string, unknown>;
  system?: Record<string, unknown>;
  ai?: Record<string, unknown>;
}

/**
 * dotted-path('a.b.c')로 obj에 value를 기록한다(중첩 경로 생성). 단일 키('a')도 지원.
 * 경로 중간 객체가 없으면 빈 객체를 만들며 내려간다. proto guard로 위험 세그먼트 차단.
 *
 * 예: setByPath({}, 'a.b', 1) → { a: { b: 1 } }
 *
 * ⚠️ obj를 **in-place mutate**한다 — 불변성이 필요한 호출부는 대상 객체를 먼저 복제할 것.
 */
export function setByPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  if (path === '') return;
  const segments = path.split('.');
  let current: Record<string, unknown> = obj;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    // 프로토타입 오염 방지: 위험 세그먼트를 만나면 기록을 중단(getByPath와 동일 차단).
    if (seg === '__proto__' || seg === 'constructor' || seg === 'prototype') {
      return;
    }
    if (i === segments.length - 1) {
      current[seg] = value;
      return;
    }
    const next = current[seg];
    if (next === null || next === undefined || typeof next !== 'object') {
      current[seg] = {};
    }
    current = current[seg] as Record<string, unknown>;
  }
}

/** catalog entry.kind → compose 소스 키 매핑. data/computed=system, input=input, ai=ai. */
function sourceKeyForKind(kind: CatalogIndex[string]['kind']): keyof ComposeSources | undefined {
  switch (kind) {
    case 'input':
      return 'input';
    case 'ai':
      return 'ai';
    case 'data':
    case 'computed':
      return 'system';
    default:
      return undefined;
  }
}

/**
 * 노드 1개의 binding을 compose해 content에 기록한다(binding 없으면 skip, C3 듀얼 수용).
 *
 * - lifecycle 'live' : skip (render 시 재조회).
 * - **catalogKey 있으면(카탈로그 경로 우선)**: catalog에서 entry를 찾아 kind로 소스 맵 선택
 *   (data/computed→system, input→input, ai→ai). 값이 있으면 select ?? bindingKey 경로에 기록.
 *   (catalog 미주입이면 kind 불명 → skip. catalogKey 쓰면 catalog 제공이 정상.)
 * - 없으면 레거시: source==='const' skip / 그 외 sources[source]?.[bindingKey] → api.select ?? bindingKey.
 */
function composeBinding(
  binding: Binding,
  sources: ComposeSources,
  content: Record<string, unknown>,
  catalog?: CatalogIndex,
): void {
  // 'live'는 render 시 재조회 대상 — snapshot 안 함.
  if (binding.lifecycle === 'live') return;

  // 카탈로그 경로 우선
  if (binding.catalogKey) {
    const entry = catalog?.[binding.catalogKey];
    if (!entry) return; // catalog 미제공/미발견 → kind 불명, skip
    const sourceKey = sourceKeyForKind(entry.kind);
    if (!sourceKey) return;
    const value = sources[sourceKey]?.[binding.bindingKey];
    if (value === undefined) return;
    // 기록 경로 = render의 읽기 경로(snapshotKey)와 동일 → 라운드트립.
    setByPath(content, snapshotKey(binding), value);
    return;
  }

  // 레거시 경로(source 기반)
  // 'const'는 render가 binding.constValue를 직접 읽음 — content에 안 박음.
  if (binding.source === 'const') return;
  // source 미지정(레거시 비정상)도 skip.
  if (!binding.source) return;

  const sourceMap = sources[binding.source as keyof ComposeSources];
  const value = sourceMap?.[binding.bindingKey];
  if (value === undefined) return;

  // 기록 경로 = render의 읽기 경로(api.select ?? bindingKey)와 동일 → 라운드트립.
  setByPath(content, binding.api?.select ?? binding.bindingKey, value);
}

/** compose: 노드 트리를 resolveForRender와 동일 구조로 순회하며 binding을 content에 기록. */
function walkComposeNode(
  node: ComposerNode,
  sources: ComposeSources,
  content: Record<string, unknown>,
  catalog?: CatalogIndex,
): void {
  if (node.binding) composeBinding(node.binding, sources, content, catalog);

  switch (node.kind) {
    case 'group':
    case 'stack':
      for (const child of node.children) walkComposeNode(child, sources, content, catalog);
      break;
    case 'repeat':
      walkComposeNode(node.template, sources, content, catalog);
      break;
    case 'branch':
      // 조건 데이터 바인딩도 snapshot으로 박제한다 — composeBinding이 render 읽기 경로
      // (snapshotKey)로 content에 기록하므로, resolveForRender(content)의 branch 분기가
      // resolveBindingRaw로 같은 값을 읽어 RenderData[cb.key]로 되살린다(상세/출력 조건 평가).
      for (const cb of node.conditionBindings ?? []) {
        composeBinding(cb.binding, sources, content, catalog);
      }
      for (const slot of node.branches) {
        for (const child of slot.children) walkComposeNode(child, sources, content, catalog);
      }
      break;
    case 'table':
      for (const cell of node.cells) {
        if (cell.binding) composeBinding(cell.binding, sources, content, catalog);
        if (cell.compute) {
          // 계산 셀 입력 바인딩도 일반 셀 binding과 동일하게 snapshot으로 박제한다
          // (render의 walkNode가 같은 입력을 content에서 읽어 라운드트립). const 입력은 skip.
          for (const inp of cell.compute.inputs) {
            if (inp.binding) composeBinding(inp.binding, sources, content, catalog);
          }
        }
        if (cell.content) {
          for (const inner of cell.content) walkComposeNode(inner, sources, content, catalog);
        }
      }
      break;
    default:
      break;
  }
}

/**
 * 작성 소스(input/system/ai)를 템플릿 트리에 따라 content(snapshot)로 합성한다.
 *
 * resolveForRender와 동일한 순회 구조(group children/repeat template/table cells+content)로
 * 돌며, 각 binding의 값을 render가 읽는 경로(select ?? bindingKey)에 기록한다.
 * const/live binding은 건드리지 않으므로, 다음이 라운드트립으로 성립한다:
 *   resolveForRender(template, compose(template, sources, catalog), catalog)
 *
 * catalog는 optional — catalogKey 바인딩을 쓰는 경우에만 필요하다(레거시 source 바인딩은 불요).
 */
export function compose(
  template: ReportTemplate,
  sources: ComposeSources,
  catalog?: CatalogIndex,
): Record<string, unknown> {
  const content: Record<string, unknown> = {};
  for (const node of template.body) walkComposeNode(node, sources, content, catalog);
  return content;
}
